/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */

import {
    IAgentPlugin,
    IPluginMethodMap,
} from "@vlinder-web/plugin-agent-service-react";
import config from "../config/featuresSection.config.json";
import { VApplication } from "../../../application";

import { toHeaderCase } from "js-convert-case";
import { camelCase } from "lodash";

export interface IFormatProductDetailMethods extends IPluginMethodMap {
    getFormatProductDetailSync(args: any): any;
}

export class FormatProductDetailService implements IAgentPlugin {
    readonly methods: IFormatProductDetailMethods;

    constructor() {
        this.methods = {
            getFormatProductDetailSync: this.getProps.bind(this),
        };
    }

    getProps(args: any) {
        console.log(`args`, args);
        return {
            productData: {
                title: toHeaderCase(args?.ProvDetailInfo?.title ?? ""),
                subtitle: args?.ProvDetailInfo?.aboutProvInfo,
                backArrow: true,
                heroImage: {
                    title: args?.ProvDetailInfo?.title,
                    align: "top",
                    image: args?.logo ?? args?.org?.logo,
                },
                buttons: this.getButtons(args?.credentialURL),
                carouselItems: this.getCarousel(
                    args?.ProvStepImages?.provImages,
                    args?.ChipList?.chipItems,
                    args?.provMapProps
                ),
                carouselStyles: {
                    width: "250px",
                    height: "250px",
                },
                accordionItems: this.getAccordionItems(args),
                badgeCarouselItems: this.getBadgeCarousels(args?.badges),
                TIME_LINE_ITEMS: this.getTimeLine(args?.provenance),
            },
        };
    }

    private getBadgeCarousels(data: any) {
        let _temp: any = [];
        Array.isArray(data) &&
            data.forEach((element, index) => {
                _temp.push({
                    batchData: element,
                    key: element?.badgeName ? camelCase(element?.badgeName) : index + 1,
                    titleImage:
                        element?.images &&
                            Array.isArray(element?.images) &&
                            element?.images.length > 0
                            ? element?.images[0]
                            : "https://i.ibb.co/WBztXW5/organic-logo-removebg-preview.png",
                    title: element?.badgeName,
                });
            });

        return _temp;
    }

    private getCarousel(imgdata: any, evidata: any, mapDetails: any) {
        const imgs =
            imgdata?.map((item: any) => {
                return {
                    type: "img",
                    src: item?.image,
                };
            }) ?? [];
        const evid =
            evidata?.map((item: any) => {
                let _formatter = item?.docUri.split(".")
                _formatter = _formatter.at(-1)
                console.log(`_formatter :`, _formatter)
                if (_formatter === "pdf") {
                    return {
                        type: "pdf",
                        src: item?.docUri,
                        title: item?.title,
                    };
                } else if (_formatter === "xlsx") {
                    return {
                        type: "xlsx",
                        src: item?.docUri,
                        title: item?.title,
                    };
                } else {
                    return {
                        type: "img",
                        src: item?.docUri,
                        title: item?.title,
                    };
                }
            }) ?? [];
        let _map = null;
        mapDetails?.geojson &&
            mapDetails?.geojson?.features &&
            Array.isArray(mapDetails?.geojson?.features) &&
            mapDetails?.geojson?.features.length > 0 &&
            mapDetails?.geojson?.features.forEach((item) => {
                const coordinates =
                    mapDetails?.geojson?.features[0]?.geometry?.coordinates;
                const __map = {
                    type: "map",
                    center: { lat: coordinates[0], lng: coordinates[1] },
                    zoom: 11,
                    geojson: mapDetails?.geojson,
                };
                _map = __map;
            });
        const _carsoul = [...imgs, ...evid, _map];
        console.log(`_carsoul`, _carsoul, mapDetails);
        return _carsoul?.length > 0 ? _carsoul : null;
    }

    private getTimeLine(data: any) {
        let _temp: any = [];
        Array.isArray(data) &&
            data?.forEach((element) => {
                _temp.push({
                    key: element?._id,
                    headline: toHeaderCase(element?.name ?? ""),
                    headlineBackgroundColor: "#579783",
                    children: this.getChildren(element),
                });
            });

        return _temp;
    }

    private getChildren(data: any) {
        let _temp: any = [];
        _temp.push({
            provenanceItem: data,
            itemId: data?.provenanceId,
            title: data?.name,
            subtitle: data?.description,
            carouselItems: data?.media?.length > 0 ? [...data?.media] : null,
            carouselStyles: {
                width: "250px",
                height: "250px",
            },
            badgeCarouselItems: this.getBadgeCarousel(data?.credentials),
        });

        Array.isArray(data?.subSteps) &&
            data?.subSteps.forEach((ele) => {
                _temp.push({
                    provenanceItem: ele,
                    itemId: ele?.provenanceId,
                    title: ele?.name,
                    subtitle: ele?.description,
                    carouselItems: ele?.media?.length > 0 ? [...ele?.media] : null,
                    carouselStyles: {
                        width: "250px",
                        height: "250px",
                    },
                    badgeCarouselItems: this.getBadgeCarousel(ele?.credentials),
                });
            });

        return _temp;
    }

    private getBadgeCarousel(data: any) {
        let _temp: any = [];
        Array.isArray(data) &&
            data.forEach((element) => {
                _temp.push({
                    batchData: element,
                    key: element?.batchId,
                    titleImage: element?.logo,
                    title: element?.name,
                });
            });

        return _temp;
    }

    private getAccordionItems(res: any) {
        let _temp = [];
        if (res?.description) {
            _temp.push({
                type: "text",
                title: "Description",
                data: [res?.description],
            });
        }
        if (
            res?.ProvDetailInfo?.provInfoList &&
            res?.ProvDetailInfo?.provInfoList?.length > 0
        ) {
            let _list: any = [];
            Array.isArray(res?.ProvDetailInfo?.provInfoList) &&
                res?.ProvDetailInfo?.provInfoList.forEach((ele: any) => {
                    if (ele?.label && ele?.value)
                        _list.push({
                            ...ele,
                            title: toHeaderCase(ele?.label ?? ""),
                            subTitle: ele?.value,
                        });
                });
            _temp.push({
                type: "multi-col-list",
                title: "Batch Details",
                numberOfColumns: "3",
                data: [..._list],
            });
        }
        if (res?.org) {
            let _list: any = [];
            let _org = { ...res?.org };
            delete _org._id;
            delete _org.id;
            delete _org.phones;
            delete _org.socialLinks;
            let _social =
                Array.isArray(res?.org?.socialLinks) &&
                res?.org?.socialLinks?.map((item) => {
                    return {
                        title: toHeaderCase(item?.type ?? ""),
                        subTitle: item?.url,
                        link: item?.url,
                    };
                });
            for (const [key, value] of Object.entries(_org)) {
                _list.push({
                    title: toHeaderCase(key ?? ""),
                    subTitle: value,
                });
            }
            _temp.push({
                type: "multi-col-list",
                title: "Organization",
                numberOfColumns: "3",
                data: [..._list, ..._social],
            });
        }
        if (res?.blockchainProof && res?.blockchainProof?.length > 0) {
            let _list: any = [];
            Array.isArray(res?.blockchainProof) &&
                res?.blockchainProof.forEach((ele: any) => {
                    _list.push({
                        ...ele,
                    });
                });
            _temp.push({
                type: "multi-col-list",
                title: "Blockchain Proof",
                numberOfColumns: "3",
                data: [..._list],
            });
        }
        return _temp;
    }

    private getButtons(data: any) {
        return [
            {
                key: "verify",
                title: "Verify Credential",
                color: "primary",
                link: data,
            },
        ];
    }
}
